import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    translation: {
      company: {
        name: 'WillowTec',
        homepageAria: 'WillowTec homepage',
      },
      languages: {
        aria: 'Choose language',
        changeTo: 'Change language to {{language}}',
        polish: 'Polish',
        english: 'English',
        french: 'French',
      },
      nav: {
        services: 'Services',
        technology: 'Technology',
        rails: 'Rails',
        solutions: 'Solutions',
        flexibility: 'Flexibility',
        process: 'Process',
        contact: 'Let’s talk',
      },
      flexibility: {
        eyebrow: 'Need something different?',
        title: 'If you did not find your technology or solution, we can still help.',
        description:
            'Every project has its own context. If your company already uses a specific stack, legacy system, custom workflow or a technology not listed here, we can adapt to it, understand the existing architecture and choose the right technical approach.',
        cta: 'Tell us what you need',
        aria: 'Flexible technology and solution support',
        items: [
          {
            number: '01',
            title: 'Existing systems',
            text:
                'We can join an existing project, review the current architecture and continue development without forcing a full rewrite.',
          },
          {
            number: '02',
            title: 'Different technologies',
            text:
                'If your stack is not listed, we can evaluate it, learn the project context and work with the tools that make sense for your business.',
          },
          {
            number: '03',
            title: 'Legacy applications',
            text:
                'We help maintain, refactor and modernize older applications step by step, reducing risk and keeping the product stable.',
          },
          {
            number: '04',
            title: 'Custom workflows',
            text:
                'We adapt to your processes, infrastructure, deployment flow, coding standards and collaboration model.',
          },
        ],
      },
      rails: {
        eyebrow: 'Ruby on Rails development',
        title: 'Backend and fullstack applications built with Rails.',
        description:
            'We build robust Ruby on Rails applications: API-only backends, admin panels, internal business tools and fullstack products where Rails handles both frontend and backend layers.',
        highlight: {
          title: 'From database to user interface',
          text:
              'Rails is a strong choice for products that need fast delivery, clean conventions, maintainable architecture and reliable business logic.',
        },
        capabilities: [
          {
            number: '01',
            title: 'Backend applications',
            text:
                'Business logic, service layers, REST APIs, authentication flows, background jobs and structured database-backed systems.',
          },
          {
            number: '02',
            title: 'Fullstack Rails products',
            text:
                'Complete Rails applications with server-rendered views, Hotwire-ready architecture, admin panels and production-focused workflows.',
          },
          {
            number: '03',
            title: 'API-first systems',
            text:
                'Rails APIs designed for React frontends, mobile apps, integrations, internal tools and scalable product ecosystems.',
          },
          {
            number: '04',
            title: 'Maintenance and refactoring',
            text:
                'Code cleanup, dependency updates, performance improvements, database optimization and long-term Rails support.',
          },
        ],
        tests: {
          title: 'Testing strategy',
          description:
              'We implement practical test coverage focused on business-critical flows, regressions and long-term confidence.',
          items: [
            'Minitest for lightweight Rails test suites',
            'RSpec for expressive model, request, service and feature specs',
            'Capybara for browser-level user flows',
            'FactoryBot-style test data organization',
            'System tests for full user journeys',
            'Request specs for API endpoints',
            'Model and service tests for business rules',
            'CI-ready test execution before deployment',
          ],
        },
        practices: {
          title: 'Engineering practices',
          description:
              'We keep Rails code clean, modular and ready for future changes by applying proven software engineering principles.',
          items: [
            'SOLID',
            'DRY',
            'KISS',
            'YAGNI',
            'MVC',
            'Service Objects',
            'PORO',
            'Query Objects',
            'Form Objects',
            'Background Jobs',
            'Database Indexing',
            'Code Review',
            'CI/CD',
            'Security Updates',
            'Test Pyramid',
            'Clean Migrations',
          ],
        },
      },
      hero: {
        eyebrow: 'Software development for growing businesses',
        title: 'Websites, applications and APIs built for long-term growth.',
        description:
          'WillowTec builds modern digital products using React, Ruby on Rails, SQL and SCSS. We handle development, integrations, updates, maintenance and technical improvements so your product stays reliable, fast and ready to grow.',
        primaryCta: 'Start a project',
        secondaryCta: 'Explore services',
        statsAria: 'WillowTec highlights',
        visualAria: 'Application preview placeholder',
        mockup: {
          react: 'React Interface',
          rails: 'Rails API',
          sql: 'SQL Data',
          deploy: 'deploy',
          monitor: 'monitor',
          update: 'update',
          maintain: 'maintain',
        },
        stats: [
          {
            title: 'Full-cycle',
            text: 'Development',
          },
          {
            title: 'API-first',
            text: 'Architecture',
          },
          {
            title: 'Long-term',
            text: 'Maintenance',
          },
        ],
      },
      trusted: {
        aria: 'WillowTec capabilities',
        items: ['Websites', 'Applications', 'APIs', 'SCSS UI', 'Maintenance', 'Integrations'],
      },
      services: {
        eyebrow: 'What we do',
        title: 'Everything you need to design, build, connect and improve your product.',
        description:
          'We create clean, scalable and easy-to-maintain software for companies that need more than a simple online presence.',
        cards: [
          {
            number: '01',
            title: 'Website development',
            text: 'Fast, responsive and polished websites designed to clearly present your brand, offer and business value.',
          },
          {
            number: '02',
            title: 'Custom web applications',
            text: 'Dashboards, client portals, internal tools and business applications built around your real workflow.',
          },
          {
            number: '03',
            title: 'API development',
            text: 'REST APIs, backend services, integrations and data flows that connect your product with the tools you use.',
          },
          {
            number: '04',
            title: 'Updates & maintenance',
            text: 'Ongoing improvements, bug fixes, dependency updates, security patches and long-term technical support.',
          },
        ],
      },
      technology: {
        eyebrow: 'Technology stack',
        title: 'Modern tools. Stable architecture. Clean delivery.',
        description:
          'We choose proven technologies that are flexible, dependable and widely adopted — ideal for products that need to evolve over time.',
        noteTitle: 'API-first approach',
        noteText:
          'Frontend, backend and database layers are planned to be maintainable, testable and ready for future integrations.',
        items: [
          {
            name: 'React',
            className: 'react',
            text: 'Interactive interfaces, component-based frontend architecture and modern user experiences.',
          },
          {
            name: 'Ruby on Rails',
            className: 'rails',
            text: 'Reliable backend applications, APIs, admin panels and business logic delivered efficiently.',
          },
          {
            name: 'SQL',
            className: 'sql',
            text: 'Structured, consistent and scalable data storage for business-critical products.',
          },
          {
            name: 'SCSS',
            className: 'scss',
            text: 'Maintainable styling, reusable design tokens, responsive layouts and clean UI systems.',
          },
          {
            name: 'APIs',
            className: 'api',
            text: 'REST endpoints, third-party integrations, internal services and automation workflows.',
          },
          {
            name: 'Cloud-ready',
            className: 'cloud',
            text: 'Deployment-friendly architecture prepared for hosting, scaling, monitoring and future growth.',
          },
        ],
      },
      solutions: {
        eyebrow: 'Modern solutions',
        title: 'From polished frontend to connected backend systems.',
        description:
          'We help companies build complete digital ecosystems: responsive interfaces, secure APIs, database-backed applications, admin tools, integrations and ongoing product improvements.',
        aria: 'WillowTec modern solutions',
        items: [
          'REST API design',
          'Frontend architecture',
          'Backend architecture',
          'Database modelling',
          'Admin dashboards',
          'Client portals',
          'Responsive UI',
          'Performance optimization',
          'Maintenance plans',
          'Automated testing',
          'Deployment support',
          'Technical consulting',
        ],
      },
      api: {
        eyebrow: 'API & integrations',
        title: 'Connect your application with the tools your business already uses.',
        description:
          'We design backend systems and APIs that make it easier to exchange data, automate repetitive tasks and build new product features without unnecessary complexity.',
        previewAria: 'API preview',
        features: [
          'REST API architecture',
          'Internal and external integrations',
          'Authentication-ready backend flows',
          'Structured SQL data models',
          'Admin panels and operational dashboards',
        ],
        codePreview: `GET /api/projects

{
  "status": "success",
  "data": [
    {
      "name": "Client Portal",
      "stack": ["React", "Rails", "SQL"],
      "maintenance": true
    }
  ]
}`,
      },
      process: {
        eyebrow: 'How we work',
        title: 'A clear process from first idea to continuous improvement.',
        steps: [
          {
            number: '01',
            title: 'Discover',
            text: 'We understand your goals, users, requirements and technical context.',
          },
          {
            number: '02',
            title: 'Design & plan',
            text: 'We define the structure, core features, API needs and implementation roadmap.',
          },
          {
            number: '03',
            title: 'Develop',
            text: 'We build frontend, backend, database and integrations with clean code.',
          },
          {
            number: '04',
            title: 'Improve',
            text: 'We monitor, update and maintain your product so it can keep evolving.',
          },
        ],
      },
      showcase: {
        imageAlt: 'Example WillowTec project preview',
        imagePlaceholder: 'Image placeholder',
        eyebrow: 'For potential clients',
        title: 'Professional digital products without unnecessary complexity.',
        description:
          'Whether you need a marketing website, a client portal, an admin dashboard, an API-based application or a custom internal tool, WillowTec can help you build it and keep it running smoothly.',
        cta: 'Discuss your idea',
      },
      contact: {
        eyebrow: 'Let’s build something useful',
        title: 'Have a project in mind?',
        description:
          'Tell us what you want to create, improve or maintain. We’ll help you choose the right technical direction and plan the next steps.',
        name: 'Name',
        namePlaceholder: '[your-name]',
        email: 'Email',
        emailPlaceholder: '[your-email]',
        message: 'Project details',
        messagePlaceholder: 'Tell us about your project',
        submit: 'Send message',
        sending: 'Sending...'
      },
      footer: {
        description:
          'WillowTec is a software development company creating websites, web applications, APIs and maintainable digital systems for modern businesses.',
        companyTitle: 'Company',
        servicesTitle: 'Services',
        infoTitle: 'Basic info',
        services: ['Web development', 'React applications', 'Ruby on Rails APIs', 'Maintenance'],
        info: {
          address: 'Address: Zakrzów 60, 32-003 Zakrzów, Poland',
          email: 'Email: michalwierzbicki22@interia.eu',
          phone: 'Phone: +48 727 930 258',
          businessId: 'Business ID: 6832141518',
        },
        bottom: {
          copy: '© 2026 WillowTec. Software development, maintenance and support.',
          backToTop: 'Back to top',
          contact: 'Contact',
        },
      },
    },
  },

  pl: {
    translation: {
      company: {
        name: 'WillowTec',
        homepageAria: 'Strona główna WillowTec',
      },
      languages: {
        aria: 'Wybierz język',
        changeTo: 'Zmień język na {{language}}',
        polish: 'Polski',
        english: 'Angielski',
        french: 'Francuski',
      },
      nav: {
        services: 'Usługi',
        technology: 'Technologie',
        rails: 'Rails',
        solutions: 'Rozwiązania',
        flexibility: 'Elastyczność',
        process: 'Proces',
        contact: 'Porozmawiajmy',
      },
      flexibility: {
        eyebrow: 'Potrzebujesz czegoś innego?',
        title: 'Jeśli nie znalazłeś technologii lub rozwiązania, którego potrzebujesz, nadal możemy pomóc.',
        description:
            'Każdy projekt ma własny kontekst. Jeśli Twoja firma korzysta już z konkretnego stacku, starszego systemu, niestandardowego procesu lub technologii niewymienionej na stronie, możemy się do niej dostosować, zrozumieć istniejącą architekturę i dobrać właściwe podejście techniczne.',
        cta: 'Opowiedz, czego potrzebujesz',
        aria: 'Elastyczne wsparcie technologii i rozwiązań',
        items: [
          {
            number: '01',
            title: 'Istniejące systemy',
            text:
                'Możemy dołączyć do istniejącego projektu, przeanalizować obecną architekturę i kontynuować rozwój bez wymuszania pełnego przepisywania aplikacji.',
          },
          {
            number: '02',
            title: 'Inne technologie',
            text:
                'Jeśli Twojego stacku nie ma na liście, możemy go ocenić, poznać kontekst projektu i pracować z narzędziami, które mają sens dla Twojego biznesu.',
          },
          {
            number: '03',
            title: 'Aplikacje legacy',
            text:
                'Pomagamy utrzymywać, refaktoryzować i modernizować starsze aplikacje krok po kroku, ograniczając ryzyko i zachowując stabilność produktu.',
          },
          {
            number: '04',
            title: 'Niestandardowe procesy',
            text:
                'Dostosowujemy się do Twoich procesów, infrastruktury, sposobu wdrażania, standardów kodu i modelu współpracy.',
          },
        ],
      },
      rails: {
        eyebrow: 'Ruby on Rails development',
        title: 'Aplikacje backendowe i fullstackowe budowane w Rails.',
        description:
            'Tworzymy solidne aplikacje Ruby on Rails: backendy API-only, panele administracyjne, wewnętrzne narzędzia biznesowe oraz produkty fullstack, w których Rails obsługuje zarówno frontend, jak i backend.',
        highlight: {
          title: 'Od bazy danych po interfejs użytkownika',
          text:
              'Rails to dobry wybór dla produktów, które wymagają szybkiego dostarczania, czytelnych konwencji, utrzymywalnej architektury i stabilnej logiki biznesowej.',
        },
        capabilities: [
          {
            number: '01',
            title: 'Aplikacje backendowe',
            text:
                'Logika biznesowa, warstwy serwisowe, REST API, przepływy autoryzacji, zadania w tle oraz uporządkowane systemy oparte o bazę danych.',
          },
          {
            number: '02',
            title: 'Produkty fullstack w Rails',
            text:
                'Kompletne aplikacje Rails z widokami renderowanymi po stronie serwera, architekturą gotową pod Hotwire, panelami administracyjnymi i procesami produkcyjnymi.',
          },
          {
            number: '03',
            title: 'Systemy API-first',
            text:
                'API w Rails projektowane dla frontendów React, aplikacji mobilnych, integracji, narzędzi wewnętrznych i skalowalnych ekosystemów produktowych.',
          },
          {
            number: '04',
            title: 'Utrzymanie i refaktoryzacja',
            text:
                'Porządkowanie kodu, aktualizacje zależności, poprawa wydajności, optymalizacja bazy danych i długoterminowe wsparcie Rails.',
          },
        ],
        tests: {
          title: 'Strategia testowania',
          description:
              'Wdrażamy praktyczne pokrycie testami skupione na krytycznych procesach biznesowych, regresjach i długoterminowej pewności działania.',
          items: [
            'Minitest dla lekkich zestawów testów Rails',
            'RSpec dla czytelnych testów modeli, requestów, serwisów i funkcji',
            'Capybara dla przepływów użytkownika na poziomie przeglądarki',
            'Organizacja danych testowych w stylu FactoryBot',
            'Testy systemowe dla pełnych ścieżek użytkownika',
            'Request specs dla endpointów API',
            'Testy modeli i serwisów dla reguł biznesowych',
            'Uruchamianie testów w CI przed wdrożeniem',
          ],
        },
        practices: {
          title: 'Dobre praktyki inżynierskie',
          description:
              'Utrzymujemy kod Rails jako czytelny, modularny i gotowy na przyszłe zmiany dzięki sprawdzonym zasadom tworzenia oprogramowania.',
          items: [
            'SOLID',
            'DRY',
            'KISS',
            'YAGNI',
            'MVC',
            'Service Objects',
            'PORO',
            'Query Objects',
            'Form Objects',
            'Background Jobs',
            'Indeksy baz danych',
            'Code Review',
            'CI/CD',
            'Aktualizacje bezpieczeństwa',
            'Piramida testów',
            'Czyste migracje',
          ],
        },
      },
      hero: {
        eyebrow: 'Tworzenie oprogramowania dla rozwijających się firm',
        title: 'Strony, aplikacje i API budowane z myślą o długofalowym rozwoju.',
        description:
          'WillowTec tworzy nowoczesne produkty cyfrowe w React, Ruby on Rails, SQL i SCSS. Zajmujemy się developmentem, integracjami, aktualizacjami, utrzymaniem i usprawnieniami technicznymi, aby Twój produkt był stabilny, szybki i gotowy na rozwój.',
        primaryCta: 'Rozpocznij projekt',
        secondaryCta: 'Zobacz usługi',
        statsAria: 'Najważniejsze informacje o WillowTec',
        visualAria: 'Miejsce na podgląd aplikacji',
        mockup: {
          react: 'Interfejs React',
          rails: 'API Rails',
          sql: 'Dane SQL',
          deploy: 'wdrożenie',
          monitor: 'monitoring',
          update: 'aktualizacje',
          maintain: 'utrzymanie',
        },
        stats: [
          {
            title: 'Kompleksowo',
            text: 'Development',
          },
          {
            title: 'API-first',
            text: 'Architektura',
          },
          {
            title: 'Długoterminowo',
            text: 'Utrzymanie',
          },
        ],
      },
      trusted: {
        aria: 'Możliwości WillowTec',
        items: ['Strony www', 'Aplikacje', 'API', 'SCSS UI', 'Utrzymanie', 'Integracje'],
      },
      services: {
        eyebrow: 'Co robimy',
        title: 'Wszystko, czego potrzebujesz, aby zaprojektować, zbudować, połączyć i rozwijać produkt.',
        description:
          'Tworzymy przejrzyste, skalowalne i łatwe w utrzymaniu oprogramowanie dla firm, które potrzebują czegoś więcej niż prostej obecności online.',
        cards: [
          {
            number: '01',
            title: 'Tworzenie stron internetowych',
            text: 'Szybkie, responsywne i dopracowane strony, które jasno prezentują markę, ofertę i wartość biznesową.',
          },
          {
            number: '02',
            title: 'Dedykowane aplikacje webowe',
            text: 'Panele, portale klienta, narzędzia wewnętrzne i aplikacje biznesowe dopasowane do realnych procesów.',
          },
          {
            number: '03',
            title: 'Tworzenie API',
            text: 'REST API, usługi backendowe, integracje i przepływy danych łączące produkt z używanymi narzędziami.',
          },
          {
            number: '04',
            title: 'Aktualizacje i utrzymanie',
            text: 'Ciągłe usprawnienia, poprawki błędów, aktualizacje zależności, poprawki bezpieczeństwa i długoterminowe wsparcie techniczne.',
          },
        ],
      },
      technology: {
        eyebrow: 'Stack technologiczny',
        title: 'Nowoczesne narzędzia. Stabilna architektura. Czyste wdrożenie.',
        description:
          'Wybieramy sprawdzone technologie, które są elastyczne, niezawodne i powszechnie stosowane — idealne dla produktów rozwijanych przez dłuższy czas.',
        noteTitle: 'Podejście API-first',
        noteText:
          'Warstwy frontend, backend i baza danych są planowane tak, aby były łatwe w utrzymaniu, testowaniu i gotowe na przyszłe integracje.',
        items: [
          {
            name: 'React',
            className: 'react',
            text: 'Interaktywne interfejsy, komponentowa architektura frontendu i nowoczesne doświadczenia użytkownika.',
          },
          {
            name: 'Ruby on Rails',
            className: 'rails',
            text: 'Niezawodne aplikacje backendowe, API, panele administracyjne i logika biznesowa dostarczane efektywnie.',
          },
          {
            name: 'SQL',
            className: 'sql',
            text: 'Uporządkowane, spójne i skalowalne przechowywanie danych dla produktów biznesowych.',
          },
          {
            name: 'SCSS',
            className: 'scss',
            text: 'Łatwe w utrzymaniu style, tokeny projektowe, responsywne layouty i czyste systemy UI.',
          },
          {
            name: 'API',
            className: 'api',
            text: 'Endpointy REST, integracje zewnętrzne, usługi wewnętrzne i automatyzacja procesów.',
          },
          {
            name: 'Cloud-ready',
            className: 'cloud',
            text: 'Architektura gotowa na hosting, skalowanie, monitoring i dalszy rozwój.',
          },
        ],
      },
      solutions: {
        eyebrow: 'Nowoczesne rozwiązania',
        title: 'Od dopracowanego frontendu po połączone systemy backendowe.',
        description:
          'Pomagamy firmom budować kompletne ekosystemy cyfrowe: responsywne interfejsy, bezpieczne API, aplikacje oparte o bazy danych, narzędzia administracyjne, integracje i ciągłe usprawnienia produktu.',
        aria: 'Nowoczesne rozwiązania WillowTec',
        items: [
          'Projektowanie REST API',
          'Architektura frontendu',
          'Architektura backendu',
          'Modelowanie baz danych',
          'Panele administracyjne',
          'Portale klienta',
          'Responsywny UI',
          'Optymalizacja wydajności',
          'Plany utrzymaniowe',
          'Testy automatyczne',
          'Wsparcie wdrożeniowe',
          'Konsulting techniczny',
        ],
      },
      api: {
        eyebrow: 'API i integracje',
        title: 'Połącz aplikację z narzędziami, których Twoja firma już używa.',
        description:
          'Projektujemy systemy backendowe i API, które ułatwiają wymianę danych, automatyzację powtarzalnych zadań oraz rozwijanie nowych funkcji bez zbędnej złożoności.',
        previewAria: 'Podgląd API',
        features: [
          'Architektura REST API',
          'Integracje wewnętrzne i zewnętrzne',
          'Przepływy backendowe gotowe pod autoryzację',
          'Uporządkowane modele danych SQL',
          'Panele administracyjne i operacyjne dashboardy',
        ],
        codePreview: `GET /api/projects

{
  "status": "success",
  "data": [
    {
      "name": "Client Portal",
      "stack": ["React", "Rails", "SQL"],
      "maintenance": true
    }
  ]
}`,
      },
      process: {
        eyebrow: 'Jak pracujemy',
        title: 'Przejrzysty proces od pierwszego pomysłu po ciągłe usprawnienia.',
        steps: [
          {
            number: '01',
            title: 'Analiza',
            text: 'Poznajemy cele, użytkowników, wymagania i kontekst techniczny projektu.',
          },
          {
            number: '02',
            title: 'Projekt i plan',
            text: 'Definiujemy strukturę, kluczowe funkcje, potrzeby API i roadmapę wdrożenia.',
          },
          {
            number: '03',
            title: 'Development',
            text: 'Tworzymy frontend, backend, bazę danych i integracje w oparciu o czysty kod.',
          },
          {
            number: '04',
            title: 'Rozwój',
            text: 'Monitorujemy, aktualizujemy i utrzymujemy produkt, aby mógł dalej ewoluować.',
          },
        ],
      },
      showcase: {
        imageAlt: 'Przykładowy podgląd projektu WillowTec',
        imagePlaceholder: 'Miejsce na grafikę',
        eyebrow: 'Dla potencjalnych klientów',
        title: 'Profesjonalne produkty cyfrowe bez zbędnej złożoności.',
        description:
          'Niezależnie od tego, czy potrzebujesz strony marketingowej, portalu klienta, panelu administracyjnego, aplikacji opartej o API czy dedykowanego narzędzia wewnętrznego, WillowTec pomoże Ci je zbudować i utrzymać.',
        cta: 'Omów swój pomysł',
      },
      contact: {
        eyebrow: 'Zbudujmy coś użytecznego',
        title: 'Masz projekt na myśli?',
        description:
          'Opowiedz, co chcesz stworzyć, usprawnić lub utrzymać. Pomożemy dobrać właściwy kierunek techniczny i zaplanować kolejne kroki.',
        name: 'Imię',
        namePlaceholder: '[twoje-imie]',
        email: 'Email',
        emailPlaceholder: '[twoj-email]',
        message: 'Opis projektu',
        messagePlaceholder: 'Opowiedz nam o swoim projekcie',
        submit: 'Wyślij wiadomość',
        sending: 'Wysyłanie...'
      },
      footer: {
        description:
          'WillowTec to firma software development tworząca strony internetowe, aplikacje webowe, API i łatwe w utrzymaniu systemy cyfrowe dla nowoczesnych firm.',
        companyTitle: 'Firma',
        servicesTitle: 'Usługi',
        infoTitle: 'Podstawowe informacje',
        services: ['Web development', 'Aplikacje React', 'API Ruby on Rails', 'Utrzymanie'],
        info: {
          address: 'Adres: Zakrzów 60, 32-003 Zakrzów, Polska',
          email: 'Email: michalwierzbicki22@interia.eu',
          phone: 'Telefon: +48 727 930 258',
          businessId: 'ID firmy: 6832141518',
        },
        bottom: {
          copy: '© 2026 WillowTec. Software development, utrzymanie i wsparcie.',
          backToTop: 'Do góry',
          contact: 'Kontakt',
        },
      },
    },
  },

  fr: {
    translation: {
      company: {
        name: 'WillowTec',
        homepageAria: 'Page d’accueil WillowTec',
      },
      languages: {
        aria: 'Choisir la langue',
        changeTo: 'Changer la langue en {{language}}',
        polish: 'Polonais',
        english: 'Anglais',
        french: 'Français',
      },
      nav: {
        services: 'Services',
        technology: 'Technologies',
        rails: 'Rails',
        solutions: 'Solutions',
        flexibility: 'Flexibilité',
        process: 'Processus',
        contact: 'Parlons-en',
      },
      flexibility: {
        eyebrow: 'Besoin d’autre chose ?',
        title: 'Si vous ne trouvez pas la technologie ou la solution recherchée, nous pouvons quand même vous aider.',
        description:
            'Chaque projet a son propre contexte. Si votre entreprise utilise déjà un stack spécifique, un système existant, un workflow personnalisé ou une technologie non listée ici, nous pouvons nous y adapter, comprendre l’architecture en place et choisir la bonne approche technique.',
        cta: 'Dites-nous ce dont vous avez besoin',
        aria: 'Support flexible des technologies et solutions',
        items: [
          {
            number: '01',
            title: 'Systèmes existants',
            text:
                'Nous pouvons rejoindre un projet existant, analyser l’architecture actuelle et poursuivre le développement sans imposer une réécriture complète.',
          },
          {
            number: '02',
            title: 'Technologies différentes',
            text:
                'Si votre stack n’est pas listé, nous pouvons l’évaluer, comprendre le contexte du projet et travailler avec les outils adaptés à votre activité.',
          },
          {
            number: '03',
            title: 'Applications legacy',
            text:
                'Nous aidons à maintenir, refactoriser et moderniser les anciennes applications étape par étape, en réduisant les risques et en gardant le produit stable.',
          },
          {
            number: '04',
            title: 'Workflows personnalisés',
            text:
                'Nous nous adaptons à vos processus, votre infrastructure, votre flux de déploiement, vos standards de code et votre modèle de collaboration.',
          },
        ],
      },
      rails: {
        eyebrow: 'Développement Ruby on Rails',
        title: 'Applications backend et fullstack construites avec Rails.',
        description:
            'Nous créons des applications Ruby on Rails robustes : backends API-only, panneaux d’administration, outils métier internes et produits fullstack où Rails gère à la fois le frontend et le backend.',
        highlight: {
          title: 'De la base de données à l’interface utilisateur',
          text:
              'Rails est un excellent choix pour les produits qui nécessitent une livraison rapide, des conventions claires, une architecture maintenable et une logique métier fiable.',
        },
        capabilities: [
          {
            number: '01',
            title: 'Applications backend',
            text:
                'Logique métier, couches de services, API REST, flux d’authentification, tâches en arrière-plan et systèmes structurés basés sur une base de données.',
          },
          {
            number: '02',
            title: 'Produits fullstack Rails',
            text:
                'Applications Rails complètes avec vues rendues côté serveur, architecture prête pour Hotwire, panneaux d’administration et workflows orientés production.',
          },
          {
            number: '03',
            title: 'Systèmes API-first',
            text:
                'API Rails conçues pour les frontends React, applications mobiles, intégrations, outils internes et écosystèmes produits évolutifs.',
          },
          {
            number: '04',
            title: 'Maintenance et refactoring',
            text:
                'Nettoyage du code, mises à jour des dépendances, amélioration des performances, optimisation de base de données et support Rails à long terme.',
          },
        ],
        tests: {
          title: 'Stratégie de tests',
          description:
              'Nous mettons en place une couverture de tests pratique, centrée sur les flux métier critiques, les régressions et la confiance à long terme.',
          items: [
            'Minitest pour des suites de tests Rails légères',
            'RSpec pour des tests expressifs de modèles, requests, services et fonctionnalités',
            'Capybara pour les parcours utilisateur au niveau navigateur',
            'Organisation des données de test dans un style FactoryBot',
            'Tests système pour les parcours utilisateur complets',
            'Request specs pour les endpoints API',
            'Tests de modèles et services pour les règles métier',
            'Exécution des tests en CI avant le déploiement',
          ],
        },
        practices: {
          title: 'Bonnes pratiques d’ingénierie',
          description:
              'Nous gardons le code Rails propre, modulaire et prêt pour les évolutions futures grâce à des principes éprouvés de développement logiciel.',
          items: [
            'SOLID',
            'DRY',
            'KISS',
            'YAGNI',
            'MVC',
            'Service Objects',
            'PORO',
            'Query Objects',
            'Form Objects',
            'Background Jobs',
            'Indexation de base de données',
            'Code Review',
            'CI/CD',
            'Mises à jour de sécurité',
            'Pyramide de tests',
            'Migrations propres',
          ],
        },
      },
      hero: {
        eyebrow: 'Développement logiciel pour entreprises en croissance',
        title: 'Sites web, applications et API conçus pour une croissance durable.',
        description:
          'WillowTec crée des produits numériques modernes avec React, Ruby on Rails, SQL et SCSS. Nous gérons le développement, les intégrations, les mises à jour, la maintenance et les améliorations techniques afin que votre produit reste fiable, rapide et prêt à évoluer.',
        primaryCta: 'Démarrer un projet',
        secondaryCta: 'Voir les services',
        statsAria: 'Points clés de WillowTec',
        visualAria: 'Emplacement pour aperçu d’application',
        mockup: {
          react: 'Interface React',
          rails: 'API Rails',
          sql: 'Données SQL',
          deploy: 'déployer',
          monitor: 'surveiller',
          update: 'mettre à jour',
          maintain: 'maintenir',
        },
        stats: [
          {
            title: 'Cycle complet',
            text: 'Développement',
          },
          {
            title: 'API-first',
            text: 'Architecture',
          },
          {
            title: 'Long terme',
            text: 'Maintenance',
          },
        ],
      },
      trusted: {
        aria: 'Compétences WillowTec',
        items: ['Sites web', 'Applications', 'API', 'SCSS UI', 'Maintenance', 'Intégrations'],
      },
      services: {
        eyebrow: 'Ce que nous faisons',
        title: 'Tout ce qu’il faut pour concevoir, construire, connecter et améliorer votre produit.',
        description:
          'Nous créons des logiciels clairs, évolutifs et faciles à maintenir pour les entreprises qui ont besoin de plus qu’une simple présence en ligne.',
        cards: [
          {
            number: '01',
            title: 'Développement de sites web',
            text: 'Des sites rapides, responsives et soignés pour présenter clairement votre marque, votre offre et votre valeur métier.',
          },
          {
            number: '02',
            title: 'Applications web sur mesure',
            text: 'Tableaux de bord, portails clients, outils internes et applications métier adaptés à vos vrais processus.',
          },
          {
            number: '03',
            title: 'Développement d’API',
            text: 'API REST, services backend, intégrations et flux de données reliant votre produit aux outils que vous utilisez.',
          },
          {
            number: '04',
            title: 'Mises à jour et maintenance',
            text: 'Améliorations continues, corrections, mises à jour des dépendances, correctifs de sécurité et support technique à long terme.',
          },
        ],
      },
      technology: {
        eyebrow: 'Stack technologique',
        title: 'Outils modernes. Architecture stable. Livraison propre.',
        description:
          'Nous choisissons des technologies éprouvées, flexibles, fiables et largement adoptées — idéales pour des produits amenés à évoluer.',
        noteTitle: 'Approche API-first',
        noteText:
          'Les couches frontend, backend et base de données sont conçues pour être maintenables, testables et prêtes pour de futures intégrations.',
        items: [
          {
            name: 'React',
            className: 'react',
            text: 'Interfaces interactives, architecture frontend basée sur les composants et expériences utilisateur modernes.',
          },
          {
            name: 'Ruby on Rails',
            className: 'rails',
            text: 'Applications backend fiables, API, panneaux d’administration et logique métier livrés efficacement.',
          },
          {
            name: 'SQL',
            className: 'sql',
            text: 'Stockage de données structuré, cohérent et évolutif pour les produits critiques.',
          },
          {
            name: 'SCSS',
            className: 'scss',
            text: 'Styles maintenables, tokens de design réutilisables, mises en page responsives et systèmes UI propres.',
          },
          {
            name: 'API',
            className: 'api',
            text: 'Endpoints REST, intégrations tierces, services internes et automatisation des workflows.',
          },
          {
            name: 'Cloud-ready',
            className: 'cloud',
            text: 'Architecture prête pour l’hébergement, la mise à l’échelle, le monitoring et la croissance future.',
          },
        ],
      },
      solutions: {
        eyebrow: 'Solutions modernes',
        title: 'Du frontend soigné aux systèmes backend connectés.',
        description:
          'Nous aidons les entreprises à construire des écosystèmes numériques complets : interfaces responsives, API sécurisées, applications basées sur des bases de données, outils d’administration, intégrations et améliorations continues.',
        aria: 'Solutions modernes WillowTec',
        items: [
          'Conception REST API',
          'Architecture frontend',
          'Architecture backend',
          'Modélisation de base de données',
          'Tableaux de bord admin',
          'Portails clients',
          'UI responsive',
          'Optimisation des performances',
          'Plans de maintenance',
          'Tests automatisés',
          'Support au déploiement',
          'Conseil technique',
        ],
      },
      api: {
        eyebrow: 'API et intégrations',
        title: 'Connectez votre application aux outils que votre entreprise utilise déjà.',
        description:
          'Nous concevons des systèmes backend et des API qui facilitent l’échange de données, l’automatisation des tâches répétitives et le développement de nouvelles fonctionnalités sans complexité inutile.',
        previewAria: 'Aperçu API',
        features: [
          'Architecture REST API',
          'Intégrations internes et externes',
          'Flux backend prêts pour l’authentification',
          'Modèles de données SQL structurés',
          'Panneaux d’administration et tableaux de bord opérationnels',
        ],
        codePreview: `GET /api/projects

{
  "status": "success",
  "data": [
    {
      "name": "Client Portal",
      "stack": ["React", "Rails", "SQL"],
      "maintenance": true
    }
  ]
}`,
      },
      process: {
        eyebrow: 'Notre méthode',
        title: 'Un processus clair, de la première idée à l’amélioration continue.',
        steps: [
          {
            number: '01',
            title: 'Découvrir',
            text: 'Nous comprenons vos objectifs, vos utilisateurs, vos besoins et votre contexte technique.',
          },
          {
            number: '02',
            title: 'Concevoir et planifier',
            text: 'Nous définissons la structure, les fonctionnalités clés, les besoins API et la feuille de route.',
          },
          {
            number: '03',
            title: 'Développer',
            text: 'Nous construisons le frontend, le backend, la base de données et les intégrations avec un code propre.',
          },
          {
            number: '04',
            title: 'Améliorer',
            text: 'Nous surveillons, mettons à jour et maintenons votre produit pour qu’il continue d’évoluer.',
          },
        ],
      },
      showcase: {
        imageAlt: 'Aperçu d’un projet WillowTec',
        imagePlaceholder: 'Emplacement image',
        eyebrow: 'Pour les clients potentiels',
        title: 'Des produits numériques professionnels sans complexité inutile.',
        description:
          'Que vous ayez besoin d’un site marketing, d’un portail client, d’un tableau de bord admin, d’une application basée sur une API ou d’un outil interne sur mesure, WillowTec peut vous aider à le construire et à le maintenir.',
        cta: 'Discuter de votre idée',
      },
      contact: {
        eyebrow: 'Construisons quelque chose d’utile',
        title: 'Vous avez un projet en tête ?',
        description:
          'Dites-nous ce que vous souhaitez créer, améliorer ou maintenir. Nous vous aiderons à choisir la bonne direction technique et à planifier les prochaines étapes.',
        name: 'Nom',
        namePlaceholder: '[votre-nom]',
        email: 'Email',
        emailPlaceholder: '[votre-email]',
        message: 'Détails du projet',
        messagePlaceholder: 'Parlez-nous de votre projet',
        submit: 'Envoyer le message',
        sending: 'Envoi...'
      },
      footer: {
        description:
          'WillowTec est une société de développement logiciel qui crée des sites web, des applications web, des API et des systèmes numériques maintenables pour les entreprises modernes.',
        companyTitle: 'Entreprise',
        servicesTitle: 'Services',
        infoTitle: 'Informations de base',
        services: ['Développement web', 'Applications React', 'API Ruby on Rails', 'Maintenance'],
        info: {
          address: 'Adresse : Zakrzów 60, 32-003 Zakrzów, Pologne',
          email: 'Email : michalwierzbicki22@interia.eu',
          phone: 'Téléphone : +48 727 930 258',
          businessId: 'ID entreprise : 6832141518',
        },
        bottom: {
          copy: '© 2026 WillowTec. Développement logiciel, maintenance et support.',
          backToTop: 'Retour en haut',
          contact: 'Contact',
        },
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;